import type { AxiosResponse } from 'axios';
import * as XLSX from 'xlsx';
import { getDefaultUrl } from '~/plugins/axios';
import type { CustomFile } from '~/types';

export const viewResponseFile = (response: AxiosResponse<any, any>) => {
  const file = new Blob([response.data], {
    type: response.headers['content-type']
  });
  const fileURL = URL.createObjectURL(file);
  window.open(fileURL, '_blank', 'width=800,height=600');
};

const _renderExcelBytes = (bytes: Uint8Array, fileName: string) => {
  const workbook = XLSX.read(bytes, { type: 'array' });

  // Build per-sheet data: table html + row count (excluding header row)
  const sheetData = workbook.SheetNames.map((sheetName, i) => {
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
    const dataRows = Math.max(0, rows.length - 1); // subtract header row
    const tableHtml = XLSX.utils.sheet_to_html(sheet);
    return { sheetName, tableHtml, dataRows, index: i };
  });

  const htmlSheets = sheetData
    .map(
      ({ tableHtml, dataRows, index }) =>
        `<div class="sheet-panel ${index === 0 ? 'active' : ''}" id="panel-${index}" data-total="${dataRows}">
      <div class="toolbar">
        <div class="toolbar-left">
          <span class="meta-label">Total Records</span>
          <span class="meta-count" id="total-count-${index}">${dataRows.toLocaleString()} row${dataRows !== 1 ? 's' : ''}</span>
        </div>
        <div class="toolbar-right">
          <div class="search-box">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input
              id="gsearch-${index}"
              type="text"
              class="search-input"
              placeholder="Search all columns…"
              oninput="onGlobalSearch(${index}, this.value)"
            />
            <button class="search-clear" id="gclear-${index}" onclick="clearGlobal(${index})" title="Clear">✕</button>
          </div>
          <span class="meta-label">Rows per page</span>
          <select class="per-page-select" onchange="changePerPage(${index}, this.value)">
            <option value="10" selected>10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>
      <div class="table-wrap" id="wrap-${index}">${tableHtml}</div>
      <div class="pagination-bar" id="pg-${index}">
        <span class="pg-info" id="pg-info-${index}"></span>
        <div class="pg-controls" id="pg-ctrl-${index}"></div>
      </div>
    </div>`
    )
    .join('');

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>${fileName}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --blue:        #3b82f6;
      --blue-hover:  #2563eb;
      --blue-light:  #eff6ff;
      --blue-dark:   #1d4ed8;
      --green:       #059669;
      --green-light: #d1fae5;
      --hdr-bg:      #f8fafc;
      --hdr-text:    #1e293b;
      --hdr-border:  #cbd5e1;
      --hdr-accent:  #3b82f6;
      --row-odd:     #ffffff;
      --row-even:    #f8fafc;
      --row-hover:   #eff6ff;
      --cell-text:   #374151;
      --cell-border: #e9eef5;
      --slate-900:   #0f172a;
      --slate-700:   #334155;
      --slate-500:   #64748b;
      --slate-400:   #94a3b8;
      --slate-300:   #cbd5e1;
      --slate-200:   #e2e8f0;
      --slate-50:    #f8fafc;
      --white:       #ffffff;
      --red:         #ef4444;
      --shadow-sm:   0 1px 3px rgba(15,23,42,.06), 0 1px 2px rgba(15,23,42,.04);
      --shadow-md:   0 4px 12px rgba(15,23,42,.08), 0 2px 4px rgba(15,23,42,.04);
      --radius:      10px;
    }

    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      background: #f0f4f8;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      color: var(--cell-text);
    }

    /* ════════════════════════════════════
       TOP BAR
    ════════════════════════════════════ */
    .topbar {
      background: var(--white);
      border-bottom: 1px solid var(--slate-200);
      padding: 0 24px;
      display: flex;
      align-items: center;
      gap: 12px;
      height: 60px;
      position: sticky;
      top: 0;
      z-index: 200;
      box-shadow: var(--shadow-sm);
    }
    .file-icon {
      width: 36px; height: 36px;
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      border-radius: 9px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
      box-shadow: 0 2px 8px rgba(5,150,105,.3);
    }
    .file-icon svg { color: #fff; }
    .file-name {
      font-size: 15px;
      font-weight: 700;
      color: var(--slate-900);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 520px;
      letter-spacing: -.01em;
    }
    .file-ext {
      font-size: 10px;
      font-weight: 700;
      color: var(--green);
      background: var(--green-light);
      padding: 3px 8px;
      border-radius: 99px;
      text-transform: uppercase;
      letter-spacing: .06em;
      flex-shrink: 0;
      border: 1px solid #bbf7d0;
    }

    /* ════════════════════════════════════
       SHEET TABS
    ════════════════════════════════════ */
    .tabs-bar {
      background: var(--white);
      border-bottom: 2px solid var(--slate-200);
      padding: 0 24px;
      display: flex;
      gap: 2px;
      align-items: flex-end;
      min-height: 46px;
    }
    .tab-btn {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      padding: 8px 16px;
      border: 1px solid transparent;
      border-bottom: none;
      border-radius: 8px 8px 0 0;
      background: transparent;
      color: var(--slate-500);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      transition: all .18s;
      position: relative;
      bottom: -2px;
      font-family: inherit;
    }
    .tab-btn:hover:not(.active) {
      background: var(--slate-50);
      color: var(--slate-700);
    }
    .tab-btn.active {
      background: var(--white);
      border-color: var(--slate-200);
      border-bottom-color: var(--white);
      color: var(--blue);
      font-weight: 600;
    }
    .tab-count {
      background: var(--blue-light);
      color: var(--blue);
      font-size: 11px;
      font-weight: 700;
      padding: 2px 7px;
      border-radius: 99px;
      letter-spacing: .01em;
    }
    .tab-btn.active .tab-count {
      background: var(--blue);
      color: var(--white);
    }

    /* ════════════════════════════════════
       SHEET PANEL
    ════════════════════════════════════ */
    .sheet-panel { display: none; flex: 1; flex-direction: column; min-height: 0; }
    .sheet-panel.active { display: flex; }

    /* ════════════════════════════════════
       TOOLBAR
    ════════════════════════════════════ */
    .toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 24px;
      background: var(--white);
      border-bottom: 1px solid var(--slate-200);
      flex-wrap: wrap;
    }
    .toolbar-left { display: flex; align-items: center; gap: 10px; }
    .toolbar-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

    .meta-label {
      font-size: 12px;
      font-weight: 500;
      color: var(--slate-400);
      text-transform: uppercase;
      letter-spacing: .05em;
    }
    .meta-count {
      font-size: 13px;
      font-weight: 700;
      color: var(--blue);
      background: var(--blue-light);
      padding: 3px 10px;
      border-radius: 99px;
      border: 1px solid #bfdbfe;
    }

    /* Global search */
    .search-box {
      display: flex;
      align-items: center;
      gap: 7px;
      border: 1.5px solid var(--slate-200);
      border-radius: 8px;
      padding: 6px 12px;
      background: var(--slate-50);
      transition: all .18s;
    }
    .search-box:focus-within {
      border-color: var(--blue);
      background: var(--white);
      box-shadow: 0 0 0 3px rgba(37,99,235,.12);
    }
    .search-input {
      border: none; outline: none;
      background: transparent;
      font-size: 13px;
      font-family: inherit;
      color: var(--slate-900);
      width: 240px;
    }
    .search-input::placeholder { color: var(--slate-400); }
    .search-clear {
      border: none; background: none; cursor: pointer;
      color: var(--slate-400); font-size: 13px; padding: 0;
      line-height: 1; display: none; transition: color .15s;
    }
    .search-clear.visible { display: flex; align-items: center; }
    .search-clear:hover { color: var(--red); }

    /* Per-page select */
    .per-page-select {
      border: 1.5px solid var(--slate-200);
      border-radius: 7px;
      padding: 5px 10px;
      font-size: 13px;
      font-family: inherit;
      font-weight: 500;
      color: var(--slate-700);
      background: var(--white);
      cursor: pointer;
      outline: none;
      transition: border-color .15s;
    }
    .per-page-select:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(37,99,235,.12); }

    /* ════════════════════════════════════
       TABLE
    ════════════════════════════════════ */
    .table-wrap {
      flex: 1;
      overflow: auto;
      padding: 16px 24px 20px;
      min-height: 0;
    }
    table {
      border-collapse: collapse;
      width: 100%;
      font-size: 13.5px;
      background: var(--white);
      border-radius: var(--radius);
      overflow: hidden;
      box-shadow: var(--shadow-md), 0 0 0 1px var(--slate-200);
    }

    /* ── Header row — light, clean, professional ── */
    tr.hdr td {
      background: var(--hdr-bg);
      color: var(--hdr-text);
      font-weight: 700;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: .08em;
      padding: 12px 16px;
      white-space: nowrap;
      position: sticky;
      top: 0;
      z-index: 10;
      border-right: 1px solid var(--hdr-border);
      border-bottom: 2px solid var(--hdr-accent);
      user-select: none;
    }
    tr.hdr td:last-child { border-right: none; }

    /* ── Column filter row ── */
    tr.col-filters td {
      background: #f1f5f9;
      padding: 5px 6px;
      border-bottom: 1px solid var(--slate-200);
      position: sticky;
      top: 38px;
      z-index: 9;
      border-right: 1px solid var(--slate-200);
    }
    tr.col-filters td:last-child { border-right: none; }
    .col-filter-input {
      width: 100%;
      min-width: 64px;
      border: 1.5px solid var(--slate-300);
      border-radius: 5px;
      padding: 4px 8px;
      font-size: 12px;
      font-family: inherit;
      color: var(--slate-700);
      background: var(--white);
      outline: none;
      transition: all .15s;
    }
    .col-filter-input:focus {
      border-color: var(--blue);
      box-shadow: 0 0 0 2px rgba(59,130,246,.15);
    }
    .col-filter-input::placeholder { color: #c1cfe0; font-size: 11px; }

    /* ── Data rows ── */
    tr:not(.hdr):not(.col-filters) td {
      padding: 10px 16px;
      white-space: nowrap;
      color: var(--cell-text);
      border-bottom: 1px solid var(--cell-border);
      border-right: 1px solid var(--cell-border);
      font-size: 13px;
      line-height: 1.5;
      transition: background .1s;
    }
    tr:not(.hdr):not(.col-filters) td:last-child { border-right: none; }
    tr:not(.hdr):not(.col-filters):last-child td { border-bottom: none; }
    tr:not(.hdr):not(.col-filters):nth-child(odd)  td { background: var(--row-odd); }
    tr:not(.hdr):not(.col-filters):nth-child(even) td { background: var(--row-even); }
    tr:not(.hdr):not(.col-filters):hover td {
      background: var(--row-hover) !important;
      color: var(--slate-900);
    }

    /* ════════════════════════════════════
       PAGINATION BAR
    ════════════════════════════════════ */
    .pagination-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 24px;
      background: var(--white);
      border-top: 1px solid var(--slate-200);
      flex-shrink: 0;
      gap: 12px;
      flex-wrap: wrap;
    }
    .pg-info { font-size: 13px; color: var(--slate-500); font-weight: 400; }
    .pg-info b { color: var(--slate-900); font-weight: 700; }
    .pg-controls { display: flex; gap: 4px; align-items: center; }
    .pg-btn {
      min-width: 34px; height: 34px;
      padding: 0 10px;
      border: 1.5px solid var(--slate-200);
      border-radius: 7px;
      background: var(--white);
      color: var(--slate-700);
      font-size: 13px;
      font-weight: 500;
      font-family: inherit;
      cursor: pointer;
      display: inline-flex; align-items: center; justify-content: center;
      transition: all .15s;
    }
    .pg-btn:hover:not(:disabled) {
      background: var(--blue-light);
      border-color: var(--blue);
      color: var(--blue-hover);
    }
    .pg-btn.current {
      background: var(--blue);
      border-color: var(--blue);
      color: var(--white);
      font-weight: 700;
      box-shadow: 0 2px 8px rgba(59,130,246,.4);
    }
    .pg-btn:disabled { opacity: .3; cursor: not-allowed; }
    .pg-ellipsis { font-size: 14px; color: var(--slate-400); padding: 0 3px; letter-spacing: .1em; }
  </style>
</head>
<body>
  <div class="topbar">
    <div class="file-icon">
      <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
    </div>
    <span class="file-name">${fileName}</span>
    <span class="file-ext">${fileName.split('.').pop()}</span>
  </div>

  ${htmlSheets}

  <script>
    var state = {}; // { page, perPage, globalSearch, colFilters[] }

    // ── Init ──────────────────────────────────────────────
    document.querySelectorAll('.sheet-panel').forEach(function(panel, idx) {
      var tbl = panel.querySelector('table');
      if (!tbl) return;

      // Mark header row
      var hdrRow = tbl.querySelector('tr');
      if (hdrRow) hdrRow.classList.add('hdr');

      // Count header columns and inject column-filter row
      var hdrCells = hdrRow ? hdrRow.querySelectorAll('td') : [];
      var colCount = hdrCells.length;

      var filterRow = document.createElement('tr');
      filterRow.className = 'col-filters';
      for (var c = 0; c < colCount; c++) {
        var td = document.createElement('td');
        var inp = document.createElement('input');
        inp.type = 'text';
        inp.className = 'col-filter-input';
        inp.placeholder = '⌕ filter…';
        inp.dataset.col = c;
        inp.dataset.idx = idx;
        inp.oninput = (function(i) {
          return function() { onColFilter(i); };
        })(idx);
        td.appendChild(inp);
        filterRow.appendChild(td);
      }
      if (hdrRow && hdrRow.parentNode) {
        hdrRow.parentNode.insertBefore(filterRow, hdrRow.nextSibling);
      }

      state[idx] = { page: 1, perPage: 10, globalSearch: '', colFilters: new Array(colCount).fill('') };
      applyFilters(idx);
    });

    // ── Helpers ───────────────────────────────────────────
    function getAllDataRows(idx) {
      var tbl = document.querySelector('#panel-' + idx + ' table');
      if (!tbl) return [];
      return Array.from(tbl.querySelectorAll('tr:not(.hdr):not(.col-filters)'));
    }

    function getCellTexts(row) {
      return Array.from(row.querySelectorAll('td')).map(function(td) {
        return td.textContent.toLowerCase().trim();
      });
    }

    // ── Filter + Paginate ─────────────────────────────────
    function applyFilters(idx) {
      var s = state[idx];
      var gs = s.globalSearch.toLowerCase().trim();
      var cf = s.colFilters.map(function(v) { return v.toLowerCase().trim(); });
      var allRows = getAllDataRows(idx);

      // Determine which rows match
      var matched = allRows.filter(function(row) {
        var cells = getCellTexts(row);
        // Global search — any cell
        if (gs && !cells.some(function(c) { return c.includes(gs); })) return false;
        // Column filters — each must match its column
        for (var i = 0; i < cf.length; i++) {
          if (cf[i] && !(cells[i] || '').includes(cf[i])) return false;
        }
        return true;
      });

      // Hide all, then paginate matched
      allRows.forEach(function(r) { r.style.display = 'none'; });

      var total = matched.length;
      var totalPages = Math.max(1, Math.ceil(total / s.perPage));
      if (s.page > totalPages) s.page = 1;

      var start = (s.page - 1) * s.perPage;
      var end = Math.min(start + s.perPage, total);
      matched.slice(start, end).forEach(function(r) { r.style.display = ''; });

      renderInfo(idx, start, end, total);
      renderButtons(idx, totalPages);
    }

    function renderInfo(idx, start, end, total) {
      var el = document.getElementById('pg-info-' + idx);
      if (!el) return;
      el.innerHTML = total === 0
        ? '<span style="color:#ef4444">No matching records</span>'
        : 'Showing <b>' + (start + 1) + '</b> – <b>' + end + '</b> of <b>' + total.toLocaleString() + '</b> records';
    }

    function renderButtons(idx, totalPages) {
      var s = state[idx];
      var ctrl = document.getElementById('pg-ctrl-' + idx);
      if (!ctrl) return;
      ctrl.innerHTML = '';

      function btn(label, page, disabled, current) {
        var b = document.createElement('button');
        b.className = 'pg-btn' + (current ? ' current' : '');
        b.innerHTML = label;
        b.disabled = !!disabled;
        if (!disabled && !current) b.onclick = function() { state[idx].page = page; applyFilters(idx); };
        ctrl.appendChild(b);
      }
      function ellipsis() {
        var sp = document.createElement('span');
        sp.className = 'pg-ellipsis'; sp.textContent = '…'; ctrl.appendChild(sp);
      }

      btn('&laquo;', 1, s.page === 1, false);
      btn('&lsaquo;', s.page - 1, s.page === 1, false);

      var delta = 2, pages = [];
      for (var p = Math.max(1, s.page - delta); p <= Math.min(totalPages, s.page + delta); p++) pages.push(p);
      if (pages[0] > 1) { btn(1, 1, false, false); if (pages[0] > 2) ellipsis(); }
      pages.forEach(function(p) { btn(p, p, false, p === s.page); });
      if (pages[pages.length - 1] < totalPages) {
        if (pages[pages.length - 1] < totalPages - 1) ellipsis();
        btn(totalPages, totalPages, false, false);
      }

      btn('&rsaquo;', s.page + 1, s.page === totalPages, false);
      btn('&raquo;', totalPages, s.page === totalPages, false);
    }

    // ── Event handlers ────────────────────────────────────
    function onGlobalSearch(idx, val) {
      state[idx].globalSearch = val;
      state[idx].page = 1;
      var clearBtn = document.getElementById('gclear-' + idx);
      if (clearBtn) clearBtn.classList.toggle('visible', val.length > 0);
      applyFilters(idx);
    }

    function clearGlobal(idx) {
      state[idx].globalSearch = '';
      state[idx].page = 1;
      var inp = document.getElementById('gsearch-' + idx);
      if (inp) inp.value = '';
      var clearBtn = document.getElementById('gclear-' + idx);
      if (clearBtn) clearBtn.classList.remove('visible');
      applyFilters(idx);
    }

    function onColFilter(idx) {
      var panel = document.getElementById('panel-' + idx);
      if (!panel) return;
      var inputs = panel.querySelectorAll('.col-filter-input');
      inputs.forEach(function(inp) {
        state[idx].colFilters[parseInt(inp.dataset.col, 10)] = inp.value;
      });
      state[idx].page = 1;
      applyFilters(idx);
    }

    function changePerPage(idx, val) {
      state[idx].perPage = parseInt(val, 10);
      state[idx].page = 1;
      applyFilters(idx);
    }

    function showSheet(index) {
      document.querySelectorAll('.sheet-panel').forEach(function(p, i) {
        p.classList.toggle('active', i === index);
      });
      document.querySelectorAll('.tab-btn').forEach(function(b, i) {
        b.classList.toggle('active', i === index);
      });
    }
  </script>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'width=1100,height=700');
};

// ── Public API ─────────────────────────────────────────────────────────────

export const viewExcelFile = (response: AxiosResponse<ArrayBuffer, any>, fileName: string) => {
  const raw = response.data;
  if (!raw) throw new Error('ExcelPreviewUnavailable');

  const bytes = raw instanceof ArrayBuffer ? new Uint8Array(raw) : (raw as Uint8Array);

  const contentType = (response.headers?.['content-type'] as string) ?? '';
  if (contentType.includes('text/html') || bytes[0] === 0x3c) {
    throw new Error('ExcelPreviewUnavailable');
  }

  _renderExcelBytes(bytes, fileName);
};

export const viewLocalExcelFile = async (file: CustomFile): Promise<void> => {
  const raw = file.rawFile as File;
  const arrayBuffer = await raw.arrayBuffer();
  _renderExcelBytes(new Uint8Array(arrayBuffer), file.filename);
};

export const viewLocalFile = (file: CustomFile) => {
  const url = URL.createObjectURL(file.rawFile as Blob);
  window.open(url, '_blank', 'width=800,height=600');
};

// export const downloadResponseFile = (response: AxiosResponse<any, any>, fileName: string) => {
//   // Create blob with the correct type from response headers
//   const blob = new Blob([response.data], {
//     type: response.headers['content-type']
//   });

//   const link = document.createElement('a');
//   link.href = window.URL.createObjectURL(blob);
//   link.download = fileName;
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
//   window.URL.revokeObjectURL(link.href);
// };

export const downloadResponseFile = (response: AxiosResponse<any, any>, fileName: string) => {
  // If response.data is already a Blob, use it directly
  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], {
          type: response.headers?.['content-type']
        });

  // Try to extract filename from Content-Disposition
  const disposition = response.headers?.['content-disposition'];
  let finalFileName = fileName;

  if (disposition) {
    const match = disposition.match(/filename="?([^"]+)"?/);
    if (match?.[1]) {
      finalFileName = match[1];
    }
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = finalFileName;

  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadLocalFile = (file: CustomFile) => {
  const url = URL.createObjectURL(file.rawFile as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const shareOnWhatsApp = (identifier: string) => {
  // Get the URL at runtime when the function is called
  const baseUrl = getDefaultUrl();
  const message = encodeURIComponent(`${baseUrl}/v1/donation_receipts/${identifier}`);
  const whatsappUrl = `https://api.whatsapp.com/send?text=Dear+Donor%2C+thank+you+for+your+contribution+to+ERP4Impact+Foundation.+Please+click+the+link+below+for+your+receipt%3A+${message}`;
  window.open(whatsappUrl, '_blank');
};
